import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { studio, apiError, inputCls, btnPrimary } from "@/lib/studioApi";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export default function StudioPlanning() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [day, setDay] = useState("");
  const [form, setForm] = useState({ client_name: "", site_city: "", client_phone: "" });
  const [busy, setBusy] = useState(false);

  const month = monthKey(cursor);

  useEffect(() => {
    studio.get(`/calendar?month=${month}`).then(({ data }) => setEvents(data.events)).catch(() => toast.error("Chargement impossible."));
  }, [month]);

  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = (first.getDay() + 6) % 7;
    const days = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = Array.from({ length: start }, () => null);
    for (let d = 1; d <= days; d += 1) cells.push(`${month}-${String(d).padStart(2, "0")}`);
    return cells;
  }, [cursor, month]);

  const byDay = useMemo(() => {
    const map = {};
    events.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [events]);

  const createIntervention = async () => {
    if (!form.client_name.trim()) return toast.error("Indiquez le nom du client.");
    setBusy(true);
    try {
      const { data } = await studio.post("/interventions", { ...form, date: day });
      toast.success(`Intervention ${data.reference} planifiée.`);
      navigate(`/studio/interventions/${data.id}`);
    } catch (e) { toast.error(apiError(e, "Création impossible.")); }
    finally { setBusy(false); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <StudioLayout title="Planning" subtitle="Vos visites techniques et interventions du mois. Touchez un jour pour y ajouter une intervention.">
      <div className="mt-6 flex items-center justify-between gap-3">
        <button type="button" data-testid="planning-prev" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="rounded-xl border border-[#E2E7F7] bg-white p-2 text-[#5C6A87] hover:text-[#3A5CC8] hover:border-[#3A5CC8] transition-colors duration-200">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <p data-testid="planning-month" className="font-heading text-base font-semibold text-[#0F2645]">
          {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
        </p>
        <button type="button" data-testid="planning-next" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="rounded-xl border border-[#E2E7F7] bg-white p-2 text-[#5C6A87] hover:text-[#3A5CC8] hover:border-[#3A5CC8] transition-colors duration-200">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-7 gap-1 text-center">
        {DAYS.map((d) => <p key={d} className="text-[10px] font-bold uppercase tracking-wide text-[#93A0BC]">{d}</p>)}
        {grid.map((iso, i) => (
          <button
            key={iso || `empty-${i}`}
            type="button"
            disabled={!iso}
            data-testid={iso ? `planning-day-${iso}` : undefined}
            onClick={() => setDay(iso === day ? "" : iso)}
            className={`min-h-[62px] rounded-xl border p-1 text-left transition-colors duration-200 ${
              !iso ? "border-transparent" : iso === day ? "border-[#3A5CC8] bg-[#EEF1FC]" : iso === today ? "border-[#3A5CC8]/40 bg-white" : "border-[#E2E7F7] bg-white hover:border-[#E2E7F7]"
            }`}
          >
            {iso && (
              <>
                <span className={`text-[11px] font-bold ${iso === today ? "text-[#3A5CC8]" : "text-[#5C6A87]"}`}>{Number(iso.slice(-2))}</span>
                <span className="mt-0.5 block space-y-0.5">
                  {(byDay[iso] || []).slice(0, 2).map((e) => (
                    <span key={e.id} className={`block truncate rounded px-1 text-[9px] font-semibold ${e.type === "visite" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-[#3A5CC8]"}`}>
                      {e.time ? `${e.time} ` : ""}{e.reference}
                    </span>
                  ))}
                  {(byDay[iso] || []).length > 2 && <span className="block text-[9px] font-bold text-[#93A0BC]">+{byDay[iso].length - 2}</span>}
                </span>
              </>
            )}
          </button>
        ))}
      </div>

      {day && (
        <div data-testid="planning-day-panel" className="mt-6 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <div className="flex items-center justify-between gap-3">
            <p className="font-heading text-base font-semibold text-[#0F2645]">{day.split("-").reverse().join("/")}</p>
            <button type="button" onClick={() => setDay("")} className="p-1 text-[#93A0BC] hover:text-[#0F2645]"><X className="w-4 h-4" /></button>
          </div>

          <div className="mt-3 space-y-2">
            {(byDay[day] || []).map((e) => (
              <Link key={e.id} to={e.url} className="block rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] px-4 py-3 hover:border-[#3A5CC8] transition-colors duration-200">
                <p className="text-sm font-semibold text-[#0F2645]">{e.time ? `${e.time} · ` : ""}{e.title}</p>
                <p className="text-xs text-[#5C6A87]">{e.reference}{e.subtitle ? ` · ${e.subtitle}` : ""}{e.done ? " · signée" : ""}</p>
              </Link>
            ))}
            {!(byDay[day] || []).length && <p className="text-sm text-[#5C6A87]">Rien de prévu ce jour.</p>}
          </div>

          <div className="mt-4 border-t border-[#E2E7F7] pt-4">
            <p className="text-sm font-semibold text-[#0F2645]">Ajouter une intervention ce jour</p>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input data-testid="planning-client-name" className={inputCls} placeholder="Nom du client *" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
              <input data-testid="planning-client-phone" className={inputCls} placeholder="Téléphone" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} />
              <input data-testid="planning-client-city" className={inputCls} placeholder="Ville" value={form.site_city} onChange={(e) => setForm({ ...form, site_city: e.target.value })} />
            </div>
            <button type="button" data-testid="planning-create-intervention" disabled={busy} onClick={createIntervention} className={`${btnPrimary} mt-3`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Planifier l'intervention
            </button>
          </div>
        </div>
      )}
    </StudioLayout>
  );
}
